import React, { useState, useEffect } from 'react';
import { MessageCircle, Plus, Edit2, Trash2, Save, X, ArrowUp, ArrowDown, ChevronRight, Key } from 'lucide-react';
import axios from 'axios';
import KeywordResponsesManager from './KeywordResponsesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BungyManManager = () => {
  const [activeTab, setActiveTab] = useState('flow');
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNode, setEditingNode] = useState(null);
  const [editFormData, setEditFormData] = useState({ text: '', options: [] });
  const [addingOption, setAddingOption] = useState(false);
  const [newOption, setNewOption] = useState({ text: '', responseText: '' });
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [modifiedResponseNodes, setModifiedResponseNodes] = useState({});

  useEffect(() => {
    fetchNodes();
  }, []);

  const fetchNodes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/bungyman/nodes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNodes(response.data);
    } catch (error) {
      console.error('Failed to fetch nodes:', error);
      alert('Failed to load chatbot configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNode = (node) => {
    setEditingNode(node._id);
    setEditFormData({
      text: node.text,
      options: [...node.options]
    });
    setModifiedResponseNodes({});
  };

  const handleSaveNode = async (nodeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Prepare the data to save
      const dataToSave = {
        text: editFormData.text,
        options: editFormData.options.map((opt, idx) => ({
          ...opt,
          order: idx
        }))
      };
      
      console.log('Saving node with data:', dataToSave);
      
      // Save the main node
      const response = await axios.put(
        `${API_URL}/api/admin/bungyman/nodes/${nodeId}`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Save response:', response.data);
      
      // Save all modified response nodes
      const modifiedNodeIds = Object.keys(modifiedResponseNodes);
      for (const modifiedNodeId of modifiedNodeIds) {
        await axios.put(
          `${API_URL}/api/admin/bungyman/nodes/${modifiedNodeId}`,
          modifiedResponseNodes[modifiedNodeId],
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setEditingNode(null);
      setModifiedResponseNodes({});
      fetchNodes();
      alert('Node and all responses updated successfully!');
    } catch (error) {
      console.error('Failed to update node:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to update node: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingNode(null);
    setEditFormData({ text: '', options: [] });
    setModifiedResponseNodes({});
    fetchNodes(); // Reload to reset any unsaved changes
  };

  const handleAddOption = async () => {
    if (!newOption.text || !newOption.responseText) {
      alert('Please fill in both option text and response');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      // Find current node - check editingNode first, then fallback to welcome node
      let currentNode = editingNode ? nodes.find(n => n._id === editingNode) : null;
      
      // If no editingNode (Quick Edit mode), use welcome node
      if (!currentNode) {
        currentNode = nodes.find(n => n.type === 'welcome');
      }
      
      if (!currentNode) {
        alert('Error: Could not find the node to add option to. Please try editing the node first.');
        return;
      }
      
      // Create the response node first
      const responseNodeData = {
        text: newOption.responseText,
        type: 'response',
        parentNodeId: currentNode.nodeId, // Use nodeId, not _id
        options: [{
          id: generateId(),
          text: 'Back to menu',
          targetNodeId: nodes.find(n => n.type === 'welcome')?.nodeId,
          order: 0
        }],
        order: editFormData.options.length
      };

      const responseNode = await axios.post(
        `${API_URL}/api/admin/bungyman/nodes`,
        responseNodeData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add the option to the current node
      const updatedOptions = [
        ...editFormData.options,
        {
          id: generateId(),
          text: newOption.text,
          targetNodeId: responseNode.data.nodeId,
          order: editFormData.options.length
        }
      ];

      setEditFormData(prev => ({ ...prev, options: updatedOptions }));
      setNewOption({ text: '', responseText: '' });
      setAddingOption(false);
      
      alert('Option added successfully! Click "Save Changes" to finalize.');
    } catch (error) {
      console.error('Failed to add option:', error);
      alert('Failed to add option: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteOption = (optionIndex) => {
    if (window.confirm('Are you sure you want to delete this option?')) {
      const updatedOptions = editFormData.options.filter((_, index) => index !== optionIndex);
      setEditFormData(prev => ({ ...prev, options: updatedOptions }));
    }
  };

  const handleMoveOption = (index, direction, isWelcome = false) => {
    if (isWelcome) {
      const welcomeNode = nodes.find(n => n.type === 'welcome');
      if (!welcomeNode) return;
      
      const newOptions = [...welcomeNode.options].sort((a, b) => a.order - b.order);
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newOptions.length) return;
      
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      
      newOptions.forEach((opt, idx) => {
        opt.order = idx;
      });
      
      const updatedNodes = nodes.map(n => 
        n._id === welcomeNode._id ? { ...n, options: newOptions } : n
      );
      setNodes(updatedNodes);
    } else {
      const newOptions = [...editFormData.options];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newOptions.length) return;
      
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      
      newOptions.forEach((opt, idx) => {
        opt.order = idx;
      });
      
      setEditFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const toggleNodeExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getChildNodes = (parentNodeId) => {
    return nodes.filter(node => node.parentNodeId === parentNodeId);
  };

  const handleDeleteNode = async (node) => {
    // Prevent deleting welcome node
    if (node.type === 'welcome') {
      alert('Cannot delete the welcome node. It is required for the chatbot to function.');
      return;
    }

    // Count child nodes
    const childCount = getChildNodes(node.nodeId).length;
    const warningMessage = childCount > 0 
      ? `This will delete "${node.text.substring(0, 30)}..." and ${childCount} child node(s). This action cannot be undone. Are you sure?`
      : `Delete "${node.text.substring(0, 30)}..."? This action cannot be undone.`;

    if (!window.confirm(warningMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${API_URL}/api/admin/bungyman/nodes/${node._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Node deleted successfully!');
      fetchNodes(); // Reload nodes
    } catch (error) {
      console.error('Failed to delete node:', error);
      alert('Failed to delete node: ' + (error.response?.data?.error || error.message));
    }
  };

  const renderNodeTree = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node._id);
    const childNodes = getChildNodes(node.nodeId);
    const isEditing = editingNode === node._id;

    return (
      <div key={node._id} className="mb-3">
        <div 
          className={`bg-gray-800 rounded-lg border ${
            node.type === 'welcome' ? 'border-yellow-500' : 'border-gray-700'
          }`}
          style={{ marginLeft: `${level * 20}px` }}
        >
          {/* Node Header */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start flex-1">
                {childNodes.length > 0 && (
                  <button
                    onClick={() => toggleNodeExpand(node._id)}
                    className="mr-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <ChevronRight 
                      size={20} 
                      className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs rounded ${
                      node.type === 'welcome' 
                        ? 'bg-yellow-500 text-black' 
                        : node.type === 'question'
                        ? 'bg-blue-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      {node.type}
                    </span>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={editFormData.text}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, text: e.target.value }))}
                      className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
                      rows={3}
                    />
                  ) : (
                    <p className="text-white whitespace-pre-wrap">{node.text}</p>
                  )}
                </div>
              </div>
              {!isEditing && (
                <div className="flex gap-2 ml-3">
                  <button
                    onClick={() => handleEditNode(node)}
                    className="p-2 text-yellow-500 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Edit this node"
                  >
                    <Edit2 size={16} />
                  </button>
                  {node.type !== 'welcome' && (
                    <button
                      onClick={() => handleDeleteNode(node)}
                      className="p-2 text-red-500 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Delete this node"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Options */}
            {node.options && node.options.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-gray-400 font-medium mb-2">Options & Responses:</p>
                {(isEditing ? editFormData.options : node.options)
                  .sort((a, b) => a.order - b.order)
                  .map((option, index) => {
                    const targetNode = option.targetNodeId ? nodes.find(n => n.nodeId === option.targetNodeId) : null;
                    return (
                      <div
                        key={option.id}
                        className="bg-gray-700 p-3 rounded-lg space-y-2"
                      >
                        {/* Option Text */}
                        <div className="flex items-center gap-2">
                          <span className="text-white font-mono text-xs bg-gray-600 px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          {isEditing ? (
                            <input
                              type="text"
                              placeholder="Option button text"
                              value={option.text}
                              onChange={(e) => {
                                const updatedOptions = [...editFormData.options];
                                updatedOptions[index] = { ...updatedOptions[index], text: e.target.value };
                                setEditFormData(prev => ({ ...prev, options: updatedOptions }));
                              }}
                              className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-yellow-500 outline-none text-sm"
                            />
                          ) : (
                            <span className="flex-1 text-white">{option.text}</span>
                          )}
                          {isEditing && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleMoveOption(index, 'up')}
                                disabled={index === 0}
                                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowUp size={16} />
                              </button>
                              <button
                                onClick={() => handleMoveOption(index, 'down')}
                                disabled={index === editFormData.options.length - 1}
                                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowDown size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteOption(index)}
                                className="p-1 text-red-500 hover:text-red-400"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Response Text */}
                        {targetNode && (
                          <div className="pl-8 border-l-2 border-gray-600">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-400">Response when user clicks this option:</p>
                              {isEditing && (
                                <button
                                  onClick={() => {
                                    const link = prompt('Enter link URL (e.g., https://example.com):');
                                    const linkText = prompt('Enter link text to display:');
                                    if (link && linkText) {
                                      const linkMarkdown = `[${linkText}](${link})`;
                                      const newText = targetNode.text + '\n\n' + linkMarkdown;
                                      const updatedNodes = nodes.map(n => 
                                        n.nodeId === targetNode.nodeId ? { ...n, text: newText } : n
                                      );
                                      setNodes(updatedNodes);
                                      setModifiedResponseNodes(prev => ({
                                        ...prev,
                                        [targetNode._id]: { text: newText }
                                      }));
                                    }
                                  }}
                                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                  title="Add hyperlink"
                                >
                                  🔗 Add Link
                                </button>
                              )}
                            </div>
                            {isEditing ? (
                              <>
                                <textarea
                                  placeholder="Response text that user will see&#10;&#10;To add links, use: [Link Text](URL)&#10;Example: [Book Now](https://example.com)"
                                  value={targetNode.text}
                                  onChange={(e) => {
                                    const newText = e.target.value;
                                    // Update nodes state
                                    const updatedNodes = nodes.map(n => 
                                      n.nodeId === targetNode.nodeId ? { ...n, text: newText } : n
                                    );
                                    setNodes(updatedNodes);
                                    // Track this node as modified
                                    setModifiedResponseNodes(prev => ({
                                      ...prev,
                                      [targetNode._id]: { text: newText }
                                    }));
                                  }}
                                  className="w-full bg-gray-600 text-white text-xs p-2 rounded border border-gray-500 focus:border-yellow-500 outline-none"
                                  rows={4}
                                />
                                <p className="text-[10px] text-gray-400 mt-1 italic">
                                  💡 Tip: Use [Text](URL) format for links. Example: [Click Here](https://example.com)
                                </p>
                              </>
                            ) : (
                              <p className="text-white text-xs whitespace-pre-wrap bg-gray-800 p-2 rounded">
                                {targetNode.text}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Add Option Form */}
            {isEditing && addingOption && (
              <div className="mt-4 p-4 bg-gray-700 rounded-lg space-y-3">
                <input
                  type="text"
                  placeholder="Option text (e.g., 'Know more')"
                  value={newOption.text}
                  onChange={(e) => setNewOption(prev => ({ ...prev, text: e.target.value }))}
                  className="w-full bg-gray-600 text-white p-2 rounded border border-gray-500 focus:border-yellow-500 outline-none"
                />
                <textarea
                  placeholder="Response text when this option is selected"
                  value={newOption.responseText}
                  onChange={(e) => setNewOption(prev => ({ ...prev, responseText: e.target.value }))}
                  className="w-full bg-gray-600 text-white p-2 rounded border border-gray-500 focus:border-yellow-500 outline-none"
                  rows={3}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddOption}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Add Option
                  </button>
                  <button
                    onClick={() => {
                      setAddingOption(false);
                      setNewOption({ text: '', responseText: '' });
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Edit Actions */}
            {isEditing && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setAddingOption(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  disabled={addingOption}
                >
                  <Plus size={16} />
                  Add Option
                </button>
                <button
                  onClick={() => handleSaveNode(node._id)}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  <Save size={16} />
                  Save Changes
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Child Nodes */}
        {isExpanded && childNodes.length > 0 && (
          <div className="mt-2">
            {childNodes.map(childNode => renderNodeTree(childNode, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const welcomeNode = nodes.find(n => n.type === 'welcome');

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <MessageCircle className="text-yellow-500" size={32} />
            <h1 className="text-3xl font-bold text-white">BungyMan Management System</h1>
          </div>
          <p className="text-gray-400">
            Manage your chatbot conversation flow and keyword-based responses.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('flow')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'flow'
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <MessageCircle size={20} />
            Conversation Flow
          </button>
          <button
            onClick={() => setActiveTab('keywords')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'keywords'
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <Key size={20} />
            Keyword Responses
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'flow' ? (
          <>
            {/* Instructions */}
            <div className="bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg p-4 mb-6">
              <h3 className="text-blue-300 font-semibold mb-2">How to Edit Nodes:</h3>
              <ul className="text-blue-200 text-sm space-y-1 list-disc list-inside">
                <li>Click the <Edit2 size={14} className="inline" /> icon to edit any node (including welcome message)</li>
                <li>Edit the node text, add/remove/reorder options, and edit response texts</li>
                <li>Click "Save Changes" to persist all modifications</li>
                <li>Use <Trash2 size={14} className="inline" /> icon to delete response nodes</li>
                <li>Expand nodes with <ChevronRight size={14} className="inline" /> to see child nodes</li>
              </ul>
            </div>

            {/* Chatbot Flow */}
            <div className="space-y-4">
              {welcomeNode && renderNodeTree(welcomeNode)}
            </div>
          </>
        ) : (
          <>
            {/* Instructions */}
            <div className="bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg p-4 mb-6">
              <h3 className="text-blue-300 font-semibold mb-2">Keyword Responses:</h3>
              <ul className="text-blue-200 text-sm space-y-1 list-disc list-inside">
                <li>Users can type questions and get automatic responses based on keywords</li>
                <li>Keywords are case-insensitive and support partial matching</li>
                <li>Higher priority responses are shown first when multiple keywords match</li>
                <li>Special formats like "BOOK123" automatically lookup bookings</li>
              </ul>
            </div>

            {/* Keyword Manager */}
            <KeywordResponsesManager />
          </>
        )}
      </div>
    </div>
  );
};

export default BungyManManager;
